/**
 * NotificationItem — Single notification row in the panel (Story 2.5).
 *
 * Accepts individual props (id, title, message, etc.) OR a notification object
 * from NotificationContext. As props soltas vinham do NotificationPanel, apagado
 * em 29/10/2026 (era um mock em localStorage): hoje todo chamador usa o objeto.
 */

import React from "react";
import { AlertCircle, AlertTriangle, Info, Trash2, Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const severityConfig = {
  critical: { icon: AlertCircle, className: "text-red-500" },
  error: { icon: AlertCircle, className: "text-red-400" },
  warning: { icon: AlertTriangle, className: "text-yellow-500" },
  info: { icon: Info, className: "text-blue-500" },
} as const;

type SeverityKey = keyof typeof severityConfig;

interface Props {
  // Individual props (sem chamador desde 29/10/2026 — ver o cabeçalho)
  id?: string;
  title?: string;
  message?: string;
  timestamp?: string;
  type?: string;
  unread?: boolean;
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onOpen?: (id: string) => void;
  // Legacy props (from NotificationContext direct usage)
  notification?: {
    id: string;
    title: string;
    body?: string | null;
    severity?: string | null;
    module?: string | null;
    is_read?: boolean;
    created_at?: string;
  };
  onRead?: (id: string) => void;
}

const NotificationItem: React.FC<Props> = (props) => {
  // Support both individual props and notification object
  const id = props.id || props.notification?.id || "";
  const title = props.title || props.notification?.title || "";
  const message = props.message || props.notification?.body || "";
  const timestamp = props.timestamp || props.notification?.created_at;
  const isUnread = props.unread ?? !(props.notification?.is_read ?? true);
  const severity = (props.type || props.notification?.severity || "info") as SeverityKey;
  const config = severityConfig[severity] || severityConfig.info;
  const Icon = config.icon;

  const handleClick = () => {
    if (props.onOpen) props.onOpen(id);
    else if (props.onMarkRead) props.onMarkRead(id);
    else if (props.onRead) props.onRead(id);
  };

  return (
    <div
      className={`w-full text-left px-3 py-2 hover:bg-accent transition-colors flex gap-2 items-start rounded-md ${
        !isUnread ? "opacity-60" : ""
      }`}
    >
      <button className="flex gap-2 items-start flex-1 min-w-0" onClick={handleClick}>
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${config.className}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{title}</p>
          {message && <p className="text-xs text-muted-foreground truncate">{message}</p>}
          {timestamp && !isNaN(new Date(timestamp).getTime()) && (
            <span className="text-[10px] text-muted-foreground">
              {formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: ptBR })}
            </span>
          )}
        </div>
      </button>
      <div className="flex gap-1 shrink-0 mt-0.5">
        {isUnread && props.onMarkRead && (
          <button
            onClick={() => props.onMarkRead!(id)}
            className="p-0.5 rounded hover:bg-muted"
            title="Marcar como lida"
          >
            <Check className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
        {props.onDelete && (
          <button
            onClick={() => props.onDelete!(id)}
            className="p-0.5 rounded hover:bg-muted"
            title="Remover"
          >
            <Trash2 className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
        {isUnread && !props.onMarkRead && (
          <span className="h-2 w-2 rounded-full bg-blue-500 mt-1.5" />
        )}
      </div>
    </div>
  );
};

export default NotificationItem;
